import React from 'react';
import { StyleSheet } from 'react-native';
import { StackSpacer, theme, useBreakpointHelpers } from '@sweetgreen/garnish';

import {
  AccountScreenBorderedSection,
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenSectionTitle,
  AccountScreenTitle,
  CreditSection,
  PaymentMethodList,
} from '@order/components';
import { useCustomer } from '@order/Customer';
import type { BillingAccount } from '@order/graphql';
import { useCustomerCredit, usePromoCode } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import type { PaymentCreditScreenProps } from '../../navigation/AppNavigation.props';

export const PaymentCreditScreen = ({
  navigation,
}: PaymentCreditScreenProps): React.ReactElement => {
  const { customer } = useCustomer();
  const { availableCredit, credits, isLoadingCredits, refetchCredits } =
    useCustomerCredit();
  const { t } = useLocalizationContext();
  const { isMobile } = useBreakpointHelpers();

  const {
    promoCode,
    promoCodeInvalid,
    isSubmittingPromoCode,
    setPromoCode,
    submitPromoCode,
  } = usePromoCode(refetchCredits);
  const isLoyaltyServicePromoCodesEnabled = useFeatureFlag(
    'CELS-507-short-loyalty-svc-promo-codes-enabled',
  );

  const handleSelectPaymentMethod = React.useCallback(
    (billingAccount: BillingAccount) => {
      navigation.navigate('Modal', {
        screen: 'PaymentMethodEdit',
        params: { paymentMethodId: billingAccount.id },
      });
    },
    [navigation],
  );

  const handleSelectAddPaymentMethod = React.useCallback(() => {
    navigation.navigate('Modal', {
      screen: 'PaymentMethodAdd',
    });
  }, [navigation]);

  const handlePressAvailableCredit = React.useCallback(() => {
    navigation.navigate('Modal', {
      screen: 'CreditDetails',
    });
  }, [navigation]);

  const handlePressAddPromoCode = React.useCallback(() => {
    navigation.navigate('Modal', {
      screen: 'CreditAdd',
    });
  }, [navigation]);

  return (
    <AccountScreenContainer screen="payment-credit">
      <AccountScreenTitle
        visibility="COMPACT"
        title={t('account.payment-promo.title')}
      />

      {/* PAYMENT */}
      <AccountScreenSectionTitle title={t('account.payment.title')} />
      <AccountScreenSection style={isMobile ? styles.mobileGrow : {}}>
        <AccountScreenBorderedSection>
          <PaymentMethodList
            items={customer.billingAccounts ?? []}
            onSelectItem={handleSelectPaymentMethod}
            onSelectAdd={handleSelectAddPaymentMethod}
          />
        </AccountScreenBorderedSection>
      </AccountScreenSection>

      {/* DIVIDER */}
      {isMobile ? (
        <StackSpacer divider={false} customSpacing={theme.spacing.xlarge} />
      ) : null}

      {/* CREDIT */}
      <AccountScreenSectionTitle
        title={t(
          isLoyaltyServicePromoCodesEnabled
            ? 'account.credit-promo.title'
            : 'account.credit.title',
        )}
      />
      <AccountScreenSection>
        <AccountScreenBorderedSection
          style={isMobile ? {} : styles.creditSpacing}
        >
          <CreditSection
            creditAmount={availableCredit}
            creditDetails={credits}
            promoCode={promoCode}
            isLoadingCredits={isLoadingCredits}
            isSubmittingPromoCode={isSubmittingPromoCode}
            hasPromoCodeError={promoCodeInvalid}
            onPressAvailableCredit={handlePressAvailableCredit}
            onPressAddPromoCode={handlePressAddPromoCode}
            setPromoCode={setPromoCode}
            onPressApplyPromoCode={submitPromoCode}
          />
        </AccountScreenBorderedSection>
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

const styles = StyleSheet.create({
  mobileGrow: {
    flexGrow: 0,
  },
  creditSpacing: {
    paddingVertical: theme.spacing.large,
  },
});
